import React from 'react';
import { Box, Button, Card, CardContent, Container, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import { ArrowBack as ArrowBackIcon, Home as HomeIcon, SearchOff as SearchOffIcon } from '@mui/icons-material';
import { useLocation, useNavigate } from 'react-router-dom';

const NotFoundPage: React.FC = () => {
  const theme = useTheme();
  const isSmDown = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const location = useLocation();

  const fromState = (location.state as any)?.from as string | undefined;

  React.useEffect(() => {
    try {
      sessionStorage.setItem('notfound_last_path', location.pathname + location.search);
    } catch {
      // ignore
    }
  }, [location.pathname, location.search]);

  React.useEffect(() => {
    if (!fromState) return;
    try {
      sessionStorage.setItem('notfound_last_from', fromState);
    } catch {
      // ignore
    }
  }, [fromState]);

  const handleBack = React.useCallback(() => {
    const safeNav = (to: string) => navigate(to);

    if (fromState) {
      safeNav(fromState);
      return;
    }

    try {
      const saved = sessionStorage.getItem('notfound_last_from');
      if (saved) {
        safeNav(saved);
        return;
      }
    } catch {
      // ignore
    }

    if (window.history.length > 1) {
      navigate(-1);
      return;
    }

    safeNav('/');
  }, [fromState, navigate]);

  const handleHome = React.useCallback(() => {
    navigate('/');
  }, [navigate]);

  return (
    <Box
      sx={(t: Theme) => ({
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        py: { xs: 3, sm: 6 },
        background: `radial-gradient(900px 500px at 15% 10%, ${t.palette.primary.main}12 0%, transparent 55%), radial-gradient(900px 500px at 85% 0%, ${t.palette.secondary?.main ? t.palette.secondary.main + '10' : t.palette.primary.main + '0B'} 0%, transparent 55%)`,
      })}
    >
      <Container maxWidth="md">
        <Card
          sx={(t: Theme) => ({
            borderRadius: 5,
            border: `1px solid ${t.palette.divider}`,
            boxShadow: 'none',
            overflow: 'hidden',
            backgroundColor: t.palette.background.paper,
          })}
        >
          <CardContent sx={{ p: { xs: 2.5, sm: 4 } }}>
            <Stack spacing={2.25} alignItems="center" textAlign="center">
              <Box
                sx={(t: Theme) => ({
                  width: isSmDown ? 72 : 88,
                  height: isSmDown ? 72 : 88,
                  borderRadius: 5,
                  display: 'grid',
                  placeItems: 'center',
                  border: `1px solid ${t.palette.divider}`,
                  backgroundColor: t.palette.action.hover,
                  color: t.palette.primary.main,
                })}
              >
                <SearchOffIcon sx={{ fontSize: isSmDown ? 34 : 40 }} />
              </Box>

              <Box sx={{ maxWidth: 520 }}>
                <Typography variant={isSmDown ? 'h5' : 'h4'} sx={{ fontWeight: 950, lineHeight: 1.1 }}>
                  404 – Nichts gefunden
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mt: 1, lineHeight: 1.7 }}>
                  Die Seite existiert nicht (oder wurde verschoben).
                </Typography>
              </Box>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.25} sx={{ width: { xs: '100%', sm: 'auto' } }}>
                <Button
                  variant="contained"
                  startIcon={<HomeIcon />}
                  onClick={handleHome}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.1,
                    fontWeight: 900,
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Zur Startseite
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={handleBack}
                  sx={{
                    borderRadius: 999,
                    px: 3,
                    py: 1.1,
                    fontWeight: 900,
                    textTransform: 'none',
                    width: { xs: '100%', sm: 'auto' },
                  }}
                >
                  Zurück
                </Button>
              </Stack>

              <Box
                sx={(t: Theme) => ({
                  width: '100%',
                  borderRadius: 4,
                  border: `1px dashed ${t.palette.divider}`,
                  p: 2,
                  backgroundColor: t.palette.action.hover,
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 2,
                  flexWrap: 'wrap',
                })}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 950, lineHeight: 1.2 }}>Tipp</Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35, lineHeight: 1.6 }}>
                    Prüfe die URL oder nutze die Navigation. Wenn du denkst, das ist ein Fehler, sag uns kurz Bescheid.
                  </Typography>
                </Box>
                <Button
                  variant="text"
                  onClick={() => navigate('/kundencenter')}
                  sx={{
                    borderRadius: 999,
                    px: 2,
                    fontWeight: 900,
                    textTransform: 'none',
                    whiteSpace: 'nowrap',
                    alignSelf: 'center',
                  }}
                >
                  Zum Kundencenter
                </Button>
              </Box>
            </Stack>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default NotFoundPage;
